'use client';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, ChevronDown, Target, X } from 'lucide-react';
import { BUSINESS_NEED_GROUPS, BUSINESS_NEED_OPTIONS, type BusinessNeedId } from '@sizzlenz/shared';
import { conciergeApi, type ConciergeBusinessType } from '@/lib/api/endpoints';
import { useSession } from '@/lib/auth-client';
import { emailValidation } from '@/lib/utils/validation';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface ConciergeEnquiryFormProps {
  /** Called after the request is sent successfully. */
  onSuccess?: () => void;
  /**
   * When false, the form does not render its own success alert and instead
   * relies on {@link onSuccess} to surface confirmation (e.g. inline on a parent
   * section). Defaults to true for standalone use.
   */
  showInlineSuccess?: boolean;
  /**
   * When true (default), the submit API requires an authenticated session, so
   * the name/email default from the signed-in user. Set false for contexts
   * where the endpoint accepts anonymous submissions and the fields should
   * start blank.
   */
  requireSession?: boolean;
  className?: string;
}

interface ConciergeEnquiryFormData {
  contactName: string;
  contactEmail: string;
  businessName: string;
  businessType: ConciergeBusinessType;
  website: string;
  goals: BusinessNeedId[];
  message: string;
}

const BUSINESS_TYPE_OPTIONS: { value: ConciergeBusinessType; label: string }[] = [
  { value: 'brand', label: 'Brand' },
  { value: 'retailer', label: 'Retailer' },
  { value: 'other', label: 'Something else' },
];

/**
 * Needs grouped for the help-with multi-select. Built once from the shared
 * {@link BUSINESS_NEED_OPTIONS} + {@link BUSINESS_NEED_GROUPS} so the full
 * Sizzle scope is represented — demand and awareness, product, retail and
 * channels, export, operations and commercial — in a stable, grouped order.
 */
const GOAL_GROUPS = BUSINESS_NEED_GROUPS.map((group) => ({
  ...group,
  options: BUSINESS_NEED_OPTIONS.filter((option) => option.group === group.id),
})).filter((group) => group.options.length > 0);

const GOAL_LABELS = new Map(BUSINESS_NEED_OPTIONS.map((option) => [option.id, option.label]));

/**
 * "Ideas & expert help" request form. Submissions are emailed to the concierge
 * inbox via {@link conciergeApi.sendEnquiry}. Shared by the
 * {@link ConciergeEnquiryDialog} and the standalone request pages.
 */
export function ConciergeEnquiryForm({
  onSuccess,
  showInlineSuccess = true,
  requireSession = true,
  className,
}: ConciergeEnquiryFormProps) {
  const { data: session } = useSession();
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ConciergeEnquiryFormData>({
    defaultValues: {
      contactName: (requireSession && session?.user?.name) || '',
      contactEmail: (requireSession && session?.user?.email) || '',
      businessName: '',
      businessType: 'brand',
      website: '',
      goals: [],
      message: '',
    },
  });

  const selectedType = watch('businessType');
  const selectedGoals = watch('goals');

  const toggleGoal = (id: BusinessNeedId) => {
    const next = selectedGoals.includes(id)
      ? selectedGoals.filter((goal) => goal !== id)
      : [...selectedGoals, id];
    setValue('goals', next, { shouldDirty: true });
  };

  const mutation = useMutation({
    mutationFn: (data: ConciergeEnquiryFormData) =>
      conciergeApi.sendEnquiry({
        contactName: data.contactName,
        contactEmail: data.contactEmail,
        businessName: data.businessName,
        businessType: data.businessType,
        website: data.website || undefined,
        goals: data.goals.length ? data.goals : undefined,
        message: data.message,
      }),
    onSuccess: () => {
      toast.success('Request sent');
      reset();
      if (showInlineSuccess) setShowSuccess(true);
      onSuccess?.();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message;
      toast.error(message || 'Failed to send your request');
    },
  });

  if (showSuccess) {
    return (
      <Alert className={cn('border-green-200 bg-green-50 text-green-800', className)}>
        <CheckCircle2 className="text-green-600" />
        <AlertTitle>Thanks, I've got it</AlertTitle>
        <AlertDescription>
          I'll take a proper look and get back to you with a tailored analysis and some suggestions.
          A copy has been sent to your email.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form
      onSubmit={handleSubmit((data) => mutation.mutate(data))}
      className={cn('space-y-4', className)}
    >
      <fieldset>
        <legend className="mb-2 text-sm font-medium leading-none">I'm a</legend>
        <div className="flex flex-wrap gap-2">
          {BUSINESS_TYPE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={cn(
                'cursor-pointer rounded-full border px-4 py-2 text-sm font-semibold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 has-[:focus-visible]:ring-offset-2',
                selectedType === option.value
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              )}
            >
              <input
                type="radio"
                value={option.value}
                className="sr-only"
                {...register('businessType', { required: true })}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <Label htmlFor="concierge-businessName">Business / Organisation</Label>
        <Input
          id="concierge-businessName"
          {...register('businessName', { required: 'Business name is required' })}
          placeholder="Your brand or store"
        />
        {errors.businessName && (
          <p className="mt-1 text-sm text-red-600">{errors.businessName.message}</p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="concierge-contactName">Your name</Label>
          <Input
            id="concierge-contactName"
            {...register('contactName', { required: 'Your name is required' })}
            placeholder="Your name"
          />
          {errors.contactName && (
            <p className="mt-1 text-sm text-red-600">{errors.contactName.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="concierge-contactEmail">Email</Label>
          <Input
            id="concierge-contactEmail"
            type="email"
            {...register('contactEmail', emailValidation)}
            placeholder="you@business.co.nz"
          />
          {errors.contactEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.contactEmail.message}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="concierge-website">Website (optional)</Label>
        <Input
          id="concierge-website"
          {...register('website')}
          placeholder="https://yourbusiness.co.nz"
        />
      </div>

      <div>
        <Label className="leading-none">What would you like help with?</Label>
        <p className="mb-2 mt-1 text-xs text-gray-500">
          Optional. Pick any that apply, from growing demand and awareness to retail, export and
          operations.
        </p>
        <GoalMultiSelect selectedIds={selectedGoals} onToggle={toggleGoal} />

        {selectedGoals.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {selectedGoals.map((id) => (
              <Badge key={id} variant="secondary" className="gap-1 pl-2">
                {GOAL_LABELS.get(id) ?? id}
                <button
                  type="button"
                  className="cursor-pointer rounded-full p-0.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                  aria-label={`Remove ${GOAL_LABELS.get(id) ?? id}`}
                  onClick={() => toggleGoal(id)}
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              </Badge>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="cursor-pointer text-gray-500"
              onClick={() => setValue('goals', [], { shouldDirty: true })}
            >
              Clear all
            </Button>
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="concierge-message">Tell me a bit more about your business</Label>
        <Textarea
          id="concierge-message"
          {...register('message', { required: 'Tell me a little about your business' })}
          placeholder="What you sell, where you're at, and anything you're stuck on. The more you share, the more useful my analysis will be."
          rows={5}
          className="min-h-[7lh]"
        />
        {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>}
      </div>

      <Button type="submit" className="w-full cursor-pointer" disabled={mutation.isPending}>
        {mutation.isPending ? 'Sending...' : 'Get my free analysis'}
      </Button>
    </form>
  );
}

/**
 * Grouped multi-select for the "help with" needs, modelled on the Inspiration
 * Feed filter: a popover trigger showing the active count, with options grouped
 * by {@link BUSINESS_NEED_GROUPS} and toggled via checkboxes.
 */
function GoalMultiSelect({
  selectedIds,
  onToggle,
}: {
  selectedIds: BusinessNeedId[];
  onToggle: (id: BusinessNeedId) => void;
}) {
  const count = selectedIds.length;
  const triggerLabel = useMemo(() => {
    if (count === 0) return 'Select what you need help with';
    return `${count} selected`;
  }, [count]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={count > 0 ? 'secondary' : 'outline'}
          className="w-full cursor-pointer justify-between rounded-xl"
        >
          <span className="inline-flex items-center gap-2">
            <Target className="size-4 text-gray-500" aria-hidden="true" />
            {triggerLabel}
          </span>
          <ChevronDown className="size-4 text-gray-400" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div
          className="max-h-80 overflow-y-auto overscroll-contain"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-5 p-3">
            {GOAL_GROUPS.map((group) => (
              <FieldSet key={group.id} className="gap-2">
                <FieldLegend className="text-sm font-bold text-gray-500">{group.label}</FieldLegend>
                <FieldGroup className="gap-0.5">
                  {group.options.map((option) => {
                    const checkboxId = `concierge-goal-${option.id}`;
                    const checked = selectedIds.includes(option.id);
                    return (
                      <Field
                        key={option.id}
                        orientation="horizontal"
                        className="rounded-lg px-2 py-2 transition-colors hover:bg-gray-50"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={checked}
                          className="cursor-pointer"
                          onCheckedChange={() => onToggle(option.id)}
                        />
                        <FieldLabel
                          htmlFor={checkboxId}
                          className="cursor-pointer text-sm font-medium"
                        >
                          {option.label}
                        </FieldLabel>
                      </Field>
                    );
                  })}
                </FieldGroup>
              </FieldSet>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
